/**
 * Engine-agnostic interaction helpers shared by both Step-4 renderers:
 * neighbor computation for hover-highlight, and label search/matching.
 */

export interface GraphEdgeLite {
  source: string;
  target: string;
}

export interface GraphNodeLite {
  id: string;
  label: string;
}

/** Opacity applied to nodes/edges that are dimmed during hover-highlight. */
export const DIMMED_OPACITY = 0.15;

/** Build an undirected adjacency map from a list of edges. */
export function buildAdjacency(edges: GraphEdgeLite[]): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    adjacency.get(a)!.add(b);
  };
  for (const edge of edges) {
    link(edge.source, edge.target);
    link(edge.target, edge.source);
  }
  return adjacency;
}

/** The node itself plus its direct neighbors. */
export function neighborhoodOf(
  id: string,
  adjacency: Map<string, Set<string>>
): Set<string> {
  const set = new Set<string>([id]);
  const neighbors = adjacency.get(id);
  if (neighbors) neighbors.forEach((n) => set.add(n));
  return set;
}

/** Case-insensitive substring match over node labels; returns matching ids. */
export function matchNodeIds(query: string, nodes: GraphNodeLite[]): Set<string> {
  const q = query.trim().toLowerCase();
  if (!q) return new Set();
  return new Set(
    nodes
      .filter((n) => (n.label || n.id).toLowerCase().includes(q))
      .map((n) => n.id)
  );
}
